import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useMemo,
  useImperativeHandle,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import "@shared/components/Grid/SnGrid.css"; // 커스텀 CSS 추가

import {
  ModuleRegistry,
  ExcelExportModule,
  RowGroupingModule,
  ClientSideRowModelModule,
  GridChartsModule,
  ColDef,
  CellFocusedEvent,
} from "ag-grid-enterprise";
import { global, useMenuStore } from "@shared/libs/Global";
import SnTab from "@shared/components/SnTab";
import { StyleSettings } from "../../../styles/StyleSettings";

import SnTabHeader from "@shared/components/SnTabHeader";

import CustomHeaderCheckbox from "@shared/components/Grid/CustomHeaderCheckbox";
import { createColumnDefs } from "@shared/components/Grid/SnGrid_ColumnDefs";
import DrawingButtons from "./DesignerButtons";
import { IHeaderParams } from "ag-grid-community";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SnButton from "@shared/components/SnButton";
import { CustomColDef } from "@shared/components/Grid/SnGrid_Types";
import { getTablebySql } from "@shared/utils/CommonUtils";
import { showResizablePopupModal } from "@widgets/CommonModal";
import { SnGridProps } from "@shared/components/GridV2/SnGridTypes";
import { SnColumnsCreate } from "@shared/components/GridV2/SnColumnsCreate";
import {
  ColumnConfig,
  RowData,
  SnGridEtcProps,
} from "@shared/components/GridV2/SnGridTypes";

export interface DrawingSnGridEvents {
  onBtnAddClick?: (id: string) => void; // 행 추가 버튼 클릭 이벤트 핸들러
  onBtnDeleteClick?: (id: string) => void; // 행 삭제 버튼 클릭 이벤트 핸들러
  onColumnConfigChanged?: (columns: any[]) => void;
  onHeaderChanged?: (headers: { controls: any[]; data: any }) => void;
  onSettingChanged?: (settings: any) => void;
}
//#endregion
interface DrawingSnGridProps {
  columnConfig: ColumnConfig[]; // 컬럼 정의
  rowData: RowData[] | null; // 행 데이터
  etcProps?: SnGridEtcProps;
  events?: DrawingSnGridEvents;
  filter?: { refs?: any[]; columns?: string[] };
  header?: React.ReactNode; // 그리드 헤더
  popupParent?: any; // 부모 엘리먼트
  headerInfo?: { controls: any[]; data: any };
  initSetting: any;
}

const DrawingSnGrid = forwardRef((props: DrawingSnGridProps, ref) => {
  // 구조 분해를 통해 props에서 필요한 속성 추출
  const {
    columnConfig = [], // 컬럼 정의
    rowData = [], // 행 데이터
    etcProps,
    events,
    header,
    filter,
    headerInfo,
    initSetting,
  } = props;
  const [errorMap, setErrorMap] = useState(new Map()); // 에러메시지
  const [columns, setColumns] = useState<any[]>([]);
  const [headerData, setHeaderData] = useState<{ controls: any[]; data: any }>({
    controls: [],
    data: {},
  });
  const [requireds, setRequireds] = useState<any>({}); /* */
  const [editCons, setEditCons] = useState<any>({}); /* */
  const [popupSettings, setPopupSettings] = useState<any>(); /* */
  const isDeleteFocusPending = useRef({
    pending: false, // 삭제 후 포커스 대기 여부
    index: 0, // 포커스할 대상 인덱스
  });
  const [isMemoizedRowDataChanged, setIsMemoizedRowDataChanged] =
    useState<any>(false);

  const [keyRowData, setKeyRowData] = useState<any[]>([]);
  const [recoveryData, setRecoveryData] = useState<any>({});
  const [initPinnedBottomRowData, setInitPinnedBottomRowData] = useState<any>(
    []
  );
  const [popupCons, setPopupCons] = useState<any>({});

  const popupContainer = useMemo(() => {
    // ID가 'ag-grid-popup-root'인 요소를 찾아 반환
    return document.querySelector("#ag-grid-popup-root");
  }, []);
  const isPopupOpen = useRef(false);
  const [seq, setSeq] = useState<number>(1);
  const filterBoxRef = useRef<{ openFilterBox: () => void } | null>(null);
  const memoizedColumnConfig = useMemo(() => columnConfig, [columnConfig]);
  const memoizedRowData = useMemo(() => rowData, [rowData]);
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

  // 전역 변수 선언

  const cdGrid = `grid${seq}`;

  const [settings, setSettings] = useState({
    title: "",
    table: "",
    defaultValue: "",
    button: "",
    defaultValueObject: null,
    buttonNode: null,
    columnsInfo: null,
  });

  //#endregion
  const { userInfo } = global();
  // State로 columnDefs 관리

  /*
  useEffect(() => {
    if (settingsData) {
      let button;
      if (settingsData.button) {
        button = convertButton(settingsData.button); // 버튼 변환
      }
      setSettings({
        ...settingsData,
        buttonNode: button,
      });
    }
  }, [settingsData]);
  */

  useEffect(() => {
    const loadColumnDefs = async () => {
      let updatedColumnDefs:
        | any[]
        | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];

      if (memoizedColumnConfig.length > 0) {
        const generatedColumnDefs = await SnColumnsCreate(
          memoizedColumnConfig,
          etcProps?.isEditable ?? true
        );
        // updatedColumnDefs = generatedColumnDefs;
        updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
          ...colDef,
        }));
        if (etcProps?.ischeckboxSelection) {
          updatedColumnDefs = [
            {
              headerName: "",
              cellRendererSelector: (params: any) => {
                if (params.node?.rowPinned) {
                  return { component: "" }; // footer는 아무것도 안 보여줌
                }
                return { component: "agCheckboxCellRenderer" };
              },
              cellEditor: "agCheckboxCellEditor",
              width: 20,
              colId: "fixCheckbox",
              field: "fixCheckbox",
              headerComponent: CustomHeaderCheckbox,
              editable: true,
              cellStyle: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
            },
            ...updatedColumnDefs,
          ];
        }
      }

      const columnDefsWithRowNumber = [
        {
          headerName: "No.", // 순번 컬럼
          valueGetter: (params: {
            node: { rowPinned: any; rowIndex: number };
          }) => {
            if (params.node.rowPinned) {
              const visibleRowCount =
                gridRef.current?.api.getDisplayedRowCount() || 0;
              return visibleRowCount;
            }
            return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
          },
          colId: "fixIndicator",
          width: 70, // 너비
          pinned: "left", // 왼쪽 고정
          cellClass: "row-number-cell", // 스타일 클래스
          suppressMovable: true, // 이동 불가
          suppressNavigable: true, // 키보드 이동(탭, 방향키) 차단
          sortable: false, // 정렬 불가
          filter: false, // 필터링 불가
        },
        ...updatedColumnDefs, // 기존 컬럼 정의
        {
          field: "block",
          hide: true,
        },
      ];
      setColumnDefs(columnDefsWithRowNumber);
      setColumns(columnConfig);
    };
    loadColumnDefs();
  }, [columnConfig]);

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter", // 헤더 필터 비활성화 (타입을 명시해야 정상 작동됨.)
      floatingFilter: false, // 플로팅 필터 비활성화
      resizable: true,
      suppressMenu: true,
      enableRowGroup: true,
      editable: false, // 수정 가능 여부를 props로 제어
      headerClass: "header-center",
    }),
    []
  );

  useEffect(() => {
    if (initSetting) setSettings(initSetting.etcProps);
  }, [initSetting]);

  //#endregion

  //#region 초기화

  const gridRef = useRef<AgGridReact>(null); // AG Grid 내부 참조

  const gridApiRef = useRef<any>(null); // AG Grid API
  const columnApiRef = useRef<any>(null); // AG Grid Column API

  //#endregion

  //#region 필터 관련 내용
  const rowNumberColumnDef = {
    headerName: "No.", // 순번 컬럼
    valueGetter: (params: { node: { rowPinned: any; rowIndex: number } }) => {
      if (params.node.rowPinned) {
        const visibleRowCount =
          gridRef.current?.api.getDisplayedRowCount() || 0;
        return visibleRowCount;
      }
      return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
    },
    colId: "fixIndicator",
    width: 70,
    pinned: "left",
    cellClass: "row-number-cell",
    suppressMovable: true,
    suppressNavigable: true,
    sortable: false,
    filter: false,
  };
  //#endregion

  //#region Indicator 관련 내용

  //#endregion

  //#region 외부에서 접근 가능한 메서드 정의
  useImperativeHandle(ref, () => ({
    getColumns: () => {
      return columns.map((c: any) => ({
        ...c,
        cd_grid: cdGrid,
      }));
    },
    getSettings: async () => {
      let defaultValue: any = {};
      if (columns.length === 1 && Object.keys(columns[0]).length === 0)
        return { ...settings, defaultValue: {} };
      const filtered = columns.filter(
        (col) =>
          col.defaultValue !== undefined &&
          col.defaultValue !== null &&
          col.defaultValue !== ""
      );
      filtered.map((col) => {
        if (col.tp_column === "SnNumberBox")
          defaultValue[col.cd_column] = col.defaultValue;
        else defaultValue[col.cd_column] = `'${col.defaultValue}'`;
      });
      const filteredDt = columns.filter((col) => col.cd_column.includes("dt_"));
      filteredDt.map((col) => {
        if (
          col.defaultValue !== undefined &&
          col.defaultValue !== null &&
          col.defaultValue !== ""
        )
          defaultValue[col.cd_column] =
            `getConvertedDate('${col.defaultValue}')`;
        else defaultValue[col.cd_column] = `getConvertedDate('0/0/0')`;
      });

      let columnsInfo;
      if (settings.columnsInfo == null) {
        columnsInfo = await getColumnsInfo(settings.table);
      } else {
        columnsInfo = settings.columnsInfo;
      }

      columnsInfo?.map((col: any) => {
        Object.keys(userInfo).forEach((key) => {
          if (col.column_name.includes(key) && key !== "cd_cust") {
            defaultValue[col.column_name] = `userInfo.${key}`;
            if (key in ["cd_emp", "cd_dept", "cd_gp", "cd_user"]) {
              defaultValue[col.column_name.replace("cd", "nm")] =
                `userInfo.${key.replace("cd", "nm")}`;
            }
          }
        });
      });
      return {
        ...settings,
        defaultValue: defaultValue,
        columnsInfo: columnsInfo,
      };
    },
    getPopupSettings: async () => {
      (() => {
        if (!popupSettings) return {};
        const newObj: any = {};
        Object.values(popupSettings).forEach((value, idx) => {
          newObj[(idx + 1).toString()] = value;
        });
        return newObj;
      })();
    },
  }));

  const getColumnsInfo = async (tablename: string) => {
    try {
      const rtn: any = await getColumnsInfo(tablename);
      return rtn;
    } catch (error) {}
  };
  //#endregion

  const onSetting = (value: any) => {
    const settings = {
      etcProps: {
        title: value.title,
        isEditable: value.isEditable ?? true,
        ischeckboxSelection: value.isCheckboxSelection ?? false,
        rowSelection: value.rowSelection ?? "single",
        entertoAddrow: value.entertoAddrow ?? false,
        rowGroupPanelShow: value.rowGroupPanelShow ?? "onlyWhenGrouping",
      },
    };

    events?.onSettingChanged && events.onSettingChanged(settings);
    setSettings(value);
  };

  /*
  const onGridBtnClick = async (key: string) => {
    if (key.includes("적용")) {
      const result = await showResizablePopupModal({
        title: `적용`,
        children: <TemplatePopup1 loadData={null} />,
      });
      if (!result || result === "close") return;
      setPopupSettings((prev: any = {}) => ({
        ...prev,
        [key]: result[0],
      }));
    } else {
      alert(key);
    }
  };
  */

  const convertButton = (value: string) => {
    const buttonLabels = value
      .split(",")
      .map((label) => label.trim())
      .filter((label) => label);

    const nodes = buttonLabels.map((label) => (
      <SnButton
        key={label}
        text={label}
        onClick={() =>
          //onGridBtnClick(label)
          {}
        }
      />
    ));
    return nodes;
  };

  let debounceTimeout: number;

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    return (...args: any[]) => {
      clearTimeout(debounceTimeout); // timeout을 해제
      debounceTimeout = window.setTimeout(() => func(...args), delay); // setTimeout 사용
    };
  };

  const recreateColumnDefs = async (columns: any[]) => {
    let updatedColumnDefs:
      | any[]
      | ((prevState: ColDef<any, any>[]) => ColDef<any, any>[]) = [];
    const generatedColumnDefs = await SnColumnsCreate(
      columns,
      etcProps?.isEditable ?? true
    );
    // updatedColumnDefs = generatedColumnDefs;
    updatedColumnDefs = generatedColumnDefs.map((colDef) => ({
      headerComponent: CustomHeader,
      ...colDef,
    }));
    if (etcProps?.ischeckboxSelection) {
      updatedColumnDefs = [
        {
          headerName: "",
          cellRendererSelector: (params: any) => {
            if (params.node?.rowPinned) {
              return { component: "" }; // footer는 아무것도 안 보여줌
            }
            return { component: "agCheckboxCellRenderer" };
          },
          cellEditor: "agCheckboxCellEditor",
          width: 20,
          colId: "fixCheckbox",
          field: "fixCheckbox",
          headerComponent: CustomHeaderCheckbox,
          editable: true,
          cellStyle: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        },
        ...updatedColumnDefs,
      ];
    }

    const columnDefsWithRowNumber = [
      {
        headerName: "No.", // 순번 컬럼
        valueGetter: (params: {
          node: { rowPinned: any; rowIndex: number };
        }) => {
          if (params.node.rowPinned) {
            const visibleRowCount =
              gridRef.current?.api.getDisplayedRowCount() || 0;
            return visibleRowCount;
          }
          return params.node.rowIndex + 1; // 일반 행에서는 순번 표시
        },
        colId: "fixIndicator",
        width: 70, // 너비
        pinned: "left", // 왼쪽 고정
        cellClass: "row-number-cell", // 스타일 클래스
        suppressMovable: true, // 이동 불가
        suppressNavigable: true, // 키보드 이동(탭, 방향키) 차단
        sortable: false, // 정렬 불가
        filter: false, // 필터링 불가
      },
      ...updatedColumnDefs, // 기존 컬럼 정의
      {
        field: "block",
        hide: true,
      },
    ];
    setColumnDefs(columnDefsWithRowNumber);
  };

  const renderGridContent = () => {
    if (!columnConfig || columnConfig.length === 0) {
      return;
    }
    return (
      <div
        className="DrawingSnGrid"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        }}
      >
        {/* 그리드 */}
        <div className="ag-theme-alpine" style={{ flex: "1 1 auto" }}>
          <AgGridReact
            rowStyle={{ fontSize: StyleSettings.font }}
            containerStyle={{ fontSize: StyleSettings.font }}
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={[]}
            headerHeight={30}
            rowHeight={30}
            defaultColDef={defaultColDef}
            groupDefaultExpanded={-1} // 그룹화할 때 전체확장: -1, 전체축소: 0
            // statusBar={statusBar} // 드래그 했을 때 sum 나오는거 일단 추가
            suppressContextMenu={true} // 기본 context 메뉴 표시 안 함
            //loadingOverlayComponent={"agLoadingOverlay"}
          />
        </div>
        <div className="relative">
          <DrawingButtons
            type="그리드"
            data={columns}
            headerData={headerData.controls}
            onAdd={(value: any[]) => {
              setColumns((prev) => {
                const merged = (
                  prev.length === 1 && Object.keys(prev[0]).length === 0
                    ? []
                    : prev
                ).concat(value[0]);
                const sorted = merged.map((col, idx) => ({
                  ...col,
                  no_seq: idx + 1,
                })); // no_seq 재조정

                recreateColumnDefs(sorted);
                events?.onColumnConfigChanged &&
                  events.onColumnConfigChanged(sorted);

                return sorted;
              });
            }}
            onChange={(value: any[]) => {
              setColumns(value);
              events?.onColumnConfigChanged &&
                events.onColumnConfigChanged(value);
              recreateColumnDefs(value);
            }}
            onchangeheader={(value: any[]) => {
              const header = { controls: value, data: {} };
              setHeaderData(header);

              events?.onHeaderChanged && events.onHeaderChanged(header);
            }}
            onSetting={onSetting}
            settings={settings}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {etcProps?.title ? (
        <SnTab
          titles={[settings.title == "" ? etcProps?.title : settings.title]}
          width={"100%"}
          header={[
            <SnTabHeader
              onBtnAddClick={events?.onBtnAddClick}
              onBtnDeleteClick={events?.onBtnDeleteClick}
              headerInfo={headerInfo}
            />,
          ]}
          body={[renderGridContent()]}
        />
      ) : (
        renderGridContent()
      )}
    </>
  );
});
export default React.memo(DrawingSnGrid);

const CustomHeader: React.FC<IHeaderParams> = (props) => {
  const { displayName, column, api } = props;
  const [hover, setHover] = useState(false);

  const hideColumn = () => {
    //api.setColumnVisible(column.getColId(), false);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center", // 텍스트는 가운데
        width: "100%",
        height: "100%",
        position: "relative", // X 버튼 절대 위치
        boxSizing: "border-box",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* 가운데 정렬 텍스트 */}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {displayName}
      </span>

      {/* X 버튼 */}
      <span
        style={{
          position: "absolute",
          top: "50%",
          right: 0, // 완전 우측
          transform: "translateY(-50%)", // 세로 가운데 정렬
          cursor: "pointer",
          zIndex: 10,
          background: "white",
          borderRadius: "50%",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center", // 아이콘 가운데 배치
          opacity: hover ? 1 : 0, // hover 시 표시
          transition: "opacity 0.2s ease",
        }}
        onClick={hideColumn}
      >
        <HighlightOffIcon fontSize="small" color="error" />
      </span>
    </div>
  );
};
